import { AxiosError } from 'axios';

import { ERROR_CODE, SYSTEM_SESSION_TIMEOUT_MESSAGE } from '@/constants/api';
import { VITE_BYPASS_TEST_MAILS_ENABLED } from '@/constants/configs';

import { CommonAPIError } from '@/types/common';
import { User } from '@/types/user';

export const sleep = (duration: number) =>
  new Promise(resolve => setTimeout(resolve, duration));

export const getAPIErrorMessage = (e: unknown, defaultMessage?: string) => {
  const error = e as AxiosError<CommonAPIError>;
  const status = error?.response?.status;

  if (status === 401 || status === 403) {
    return SYSTEM_SESSION_TIMEOUT_MESSAGE;
  }

  if (error?.code === 'ECONNABORTED') {
    return SYSTEM_SESSION_TIMEOUT_MESSAGE;
  }

  const errorTarget =
    error?.response?.data?.causedBy?.message ||
    error?.response?.data?.message ||
    error?.message;

  if (errorTarget && typeof errorTarget === 'string') {
    return errorTarget;
  }

  return defaultMessage || 'Something went wrong, please try again later.';
};

export const getHttpResponseCode = (error: unknown) => {
  return (error as AxiosError).response?.status as number;
};

export const getAPIErrorCode = (e: unknown, defaultCode?: string) => {
  const error = e as AxiosError<CommonAPIError>;

  const errorTarget = error?.response?.data?.error;

  if (errorTarget && typeof errorTarget === 'string') {
    return errorTarget;
  }

  return defaultCode || '';
};

type ErrorObject<T> = {
  [key in keyof T]: string;
};
export const getAPIFormError = <T>(e: unknown): ErrorObject<T> => {
  const error = e as AxiosError<CommonAPIError>;

  const errorObj =
    error?.response?.data?.causedBy?.message ||
    error?.response?.data?.message ||
    error?.response?.data;

  if (errorObj && typeof errorObj !== 'string') {
    return errorObj as ErrorObject<T>;
  }

  return {} as ErrorObject<T>;
};

export const getAPIDataError = <T>(e: unknown): T => {
  const error = e as AxiosError<CommonAPIError>;
  return error?.response?.data as T;
};

const TEST_EMAIL_PREFIXES = ['rfhl_onboarding_', 'rfhl_auto_'];

export const isTestUser = (user?: User) => {
  return (
    VITE_BYPASS_TEST_MAILS_ENABLED &&
    TEST_EMAIL_PREFIXES.some(prefix => user?.email?.startsWith(prefix)) &&
    !user?.email.startsWith('rfhl_onboarding_e2e') &&
    user?.email.endsWith('@yopmail.com')
  );
};

export function isAgeRestrictionError(error: unknown): boolean {
  const errorData = getAPIDataError<{ error?: string }>(error);
  return errorData?.error === ERROR_CODE.AGE_UNDER_18;
}

export function isMinorAgeRestrictionError(error: unknown): boolean {
  const errorData = getAPIDataError<{ error?: string }>(error);
  return errorData?.error === ERROR_CODE.MINOR_AGE_OVER_18;
}

export function isNonResidentMinorRejectedError(error: unknown): boolean {
  const errorData = getAPIDataError<{ error?: string }>(error);
  return errorData?.error === ERROR_CODE.NON_RESIDENT_MINOR_REJECTED;
}

export function isJointAdultAgeRestrictionError(error: unknown): boolean {
  const errorData = getAPIDataError<{ error?: string }>(error);
  return errorData?.error === ERROR_CODE.JOINT_ADULT_UNDER_18;
}

export function isThirdPartyComplianceError(error?: unknown): boolean {
  const errorData = getAPIDataError<{ error?: string }>(error);
  return errorData?.error === ERROR_CODE.THIRD_PARTY_COMPLIANCE_PEP_YES;
}

type MinorTriggeredByAdultErrorBody = {
  message?: string | { adultName?: string };
};

/** Parses `message.adultName` from send-OTP error `login.email.error.rs_minor_triggered_by_adult`. */
export function getMinorTriggeredByAdultName(
  error: unknown,
): string | undefined {
  const data = getAPIDataError<MinorTriggeredByAdultErrorBody>(error);
  const { message } = data || {};
  if (
    message &&
    typeof message === 'object' &&
    typeof message.adultName === 'string'
  ) {
    return message.adultName;
  }
  return undefined;
}

export const buildRequestParams = (
  input?: Record<string, string | number | undefined>,
): string => {
  if (!input) return '';
  const entries = Object.entries(input).filter(
    ([, value]) => value !== undefined && value !== '',
  );
  if (!entries.length) return '';
  return `?${entries.map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`).join('&')}`;
};
